/**
 * `src/platform/` — the ten auth calls (WS-C §1, §2.5), and the console's session wire
 * (decisions D19 to D25, ruling R-50).
 *
 * ⛔ WRITTEN FROM SCRATCH rather than adopted from the old console's `api/auth`, and the
 * reason was transitive rather than local: that module was thin wrappers over the old
 * console's own `api/client`, so reusing it would have re-imported that client module
 * instance — including its `window.location.href = '/login'` (see `../http/client.ts`'s
 * header). The wrappers themselves were fine; the client under them was not. These point at
 * OUR client.
 *
 * ⚠️ Every path below was read off `cp-auth-api`'s controllers, not transcribed from the old
 * console. A wire shape is only ever what the producer returns; if a field disagrees, the
 * producer wins and this file is wrong.
 *
 * ## The six session-answering calls carry the transport header (D21, R-50)
 *
 * `login`, `passkeyLogin`, `oauthExchange`, `changePassword`, `refreshTokens` and `logout` each
 * send `sessionTransportHeaders()`. The mint-route set is every caller of cp-auth-api's
 * `AuthService.mintTokenPair`, which is why `changePassword` is here: it revokes every session
 * and mints a fresh pair, and a cookie-writer census missed it (R-50). The console has no
 * invitation-accept call; that seventh server route is cp-auth-api's alone.
 *
 * Under the header cp-auth-api sets `cp_refresh_token` as an HttpOnly cookie and strips
 * `refreshToken` from the body, so the five calls that answer a session answer a
 * `CpCookieSessionResponse`. An N-1 pod mid-roll answers today's `CpTokenPairResponse` instead,
 * and `cpSessionResponseSchema` is the union of the two (R-10). `logout` answers 204 and this
 * file parses nothing from it (R-64).
 *
 * ⛔ NO `credentials` OPTION (default F7). Same origin, so the browser attaches the cookie itself.
 *
 * ⚠️ `GET /v1/auth/me/grants` is served by cp-auth-api DIRECTLY and must never be proxied
 * through site-admin-api: `PermissionsProxyService.proxy` mints its OWN service token and
 * does not forward the caller's bearer, so a proxied self-introspection route resolves the
 * SERVICE principal and returns no grants for every user while looking like a working
 * endpoint.
 */
import type { PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser';
import {
  ACTIVE_ROLE_SWITCH_PATH,
  RECOVERY_REFUSALS,
  SECOND_FACTOR_RECOVERY_ROUTES,
  cpSecondFactorVerifyAnswerSchema,
  cpSessionResponseSchema,
  secondFactorOptionsResponseSchema,
  secondFactorWaitSchema,
  type ActiveRoleSwitchRequest,
  type CpSessionResponse,
  type RecoveryBreakGlassRequest,
  type RecoveryCodeProofRequest,
  type RecoveryEmailSendRequest,
  type RecoveryEmailVerifyRequest,
  type ResourceGrantSnapshotResponse,
  type SecondFactorOptionsRequest,
  type SecondFactorOptionsResponse,
  type SecondFactorVerifyRequest,
  type SecondFactorWait,
} from '@zion/sdk-contracts';
import { ApiError, api, apiFetch } from '../http/client';
import { sessionTransportHeaders } from './session-transport';

/**
 * WebAuthn authentication options exactly as cp-auth-api returns them.
 *
 * ⛔ ALIASES THE LIBRARY'S OWN JSON TYPE rather than hand-rolling a copy, and that is a
 * correctness requirement rather than a shortcut. `passkey.service.ts` returns
 * `generateAuthenticationOptions()`'s output verbatim, and every consumer hands the value
 * straight to `startAuthentication({ optionsJSON })`. A hand-rolled copy that widened
 * `type` to `string` (instead of the literal `'public-key'`) is what forced an
 * `as unknown as` double-cast at each call site in the old console (#860) — silencing not
 * just those two fields but every future divergence. Aliasing the real type makes the
 * compiler check the seam on every build; `as unknown as` is banned repo-wide anyway.
 */
export type PasskeyLoginOptions = PublicKeyCredentialRequestOptionsJSON;

/**
 * Whatever a session-answering route returns under the transport header.
 *
 * ⛔ A UNION NOW, WHERE IT USED TO BE `{ accessToken, refreshToken }` (D23, R-10). The cookie arm
 * is `{ accessToken, expiresIn }` and carries no `refreshToken`; the body arm, which is an N-1
 * cp-auth-api pod during the migration roll, still carries one. Narrow with
 * `'refreshToken' in tokens`: a consumer that reads `tokens.refreshToken` unguarded no longer
 * compiles, which is how every caller was found.
 */
export type AuthTokens = CpSessionResponse;

/**
 * Which login methods this deployment actually offers.
 *
 * The shell renders ONLY what is enabled here — a disabled Google button on a deployment
 * without Google configured is a dead control.
 */
export interface AuthCapabilities {
  readonly password: boolean;
  readonly passkey: boolean;
  readonly oauth: { readonly google: boolean; readonly github: boolean };
}

/**
 * The server refused the credential and said so: this session is over.
 *
 * ⛔ THE CONSOLE'S OWN CLASS, and `@zion/sdk-react`'s same-named one is deliberately not imported
 * (default F9). That package is the DP shell's session machinery, on the DP's routes and cookie;
 * importing its verdict classes here would tie the console's control flow to a package it shares
 * no session with. The shared shapes are the schemas in `@zion/sdk-contracts` and nothing else.
 *
 * Thrown by `refreshTokens` on 401 and 403. `useAuth.doRefresh` answers it with `clearAuth()`.
 */
export class TerminalAuthError extends Error {
  constructor(readonly status: number) {
    super(`Session refused (${status})`);
    this.name = 'TerminalAuthError';
  }
}

/**
 * The refresh did not land, and the server did not refute the session.
 *
 * ⛔ THE SESSION IS KEPT (R-5, R-42). A 200 whose body neither union member accepts, a 5xx, a 429,
 * a 400 from an N-1 pod, a non-JSON 200 and a network failure all arrive here. A terminal verdict
 * on any of them would sign every operator out in a loop for the length of a cp-auth-api roll,
 * and the cookie in the jar is still good. `useAuth`'s boot arms one retry on it and holds
 * `isLoading`; the proactive poll and the 401 replay simply try again.
 *
 * @param status the HTTP status, or `'network'` when the request never produced one.
 */
export class TransientRefreshError extends Error {
  constructor(readonly status: number | 'network') {
    super(`Refresh did not complete (${status})`);
    this.name = 'TransientRefreshError';
  }
}

/**
 * A mint route answered 200 with a body `cpSessionResponseSchema` refuses.
 *
 * ⛔ A PLAIN MESSAGE, WITH THE `ZodError` AS ITS `cause` (R-35, R-37). Zod 4 puts a JSON array of
 * issues in `ZodError.message`, and `LoginPage`, `OAuthCallbackPage` and `ChangePasswordPage` all
 * render `displayMessage(err, …)` into an alert region. The operator gets the sentence; the
 * `cause` carries the issues for the console.
 */
export class SessionResponseError extends Error {
  constructor(message: string, options: { cause: unknown }) {
    super(message, options);
    this.name = 'SessionResponseError';
  }
}

/** What a sign-in surface shows when cp-auth-api answers a body the contract refuses. */
export const SESSION_RESPONSE_ERROR_MESSAGE =
  'The sign-in response did not match the session contract';

/**
 * How long `logout` waits before repeating a request an N-1 pod refused (R-48).
 *
 * One second. The ingress balances per request, so the next request draws another endpoint; this
 * delay is the roll's pod-swap granularity rather than a backoff on a busy server, and it stays
 * short enough that the sign-out click still feels live.
 */
export const LOGOUT_RETRY_DELAY_MS = 1_000;

/**
 * How many logout requests one sign-out sends at most, counting the first (R-48, R-62).
 *
 * Three, and it is a bound rather than a guarantee. cp-auth-api's Deployment declares no
 * `strategy`, so Kubernetes' default 25%/25% at `replicas: 2` gives surge 1 and unavailable 0:
 * a three-endpoint peak with two N-1 pods, where three draws all miss with probability (2/3)³,
 * about 30%, at that instant. Three 400s in a row leave the family and the cookie live and the
 * next page load hydrates the session; `security-audit.md` records that remainder. The two delays
 * add 2 seconds to the click at most.
 */
export const LOGOUT_ATTEMPTS = 3;

/** What `parseSessionResponse` answers: the narrowed session, or the `ZodError` that refused it. */
export type SessionParseResult =
  | { readonly ok: true; readonly session: AuthTokens }
  | { readonly ok: false; readonly cause: unknown };

/**
 * Parse a session-answering body, and hand the caller the failure rather than a verdict.
 *
 * ⛔ FULL BODY IN PHASE 1c, because a wrong body here is a security defect: this is the parse that
 * proves cp-auth-api stripped `refreshToken` from the cookie arm (chain C7 step 2). Both union
 * members are `.strict()`, so a body carrying an extra key — `refreshToken` among them — fails
 * both arms rather than being quietly accepted (default F10).
 *
 * ⛔ IT RETURNS THE FAILURE, IT DOES NOT CLASSIFY IT, and the split is the reason it is its own
 * export. The same refusal means two different things by caller: on refresh it is transient and
 * the session is kept (R-5), and on a mint route it is a `SessionResponseError` that the sign-in
 * surface shows (R-37). A parse that threw one class would force the other caller to catch and
 * re-throw.
 *
 * @param body the value `apiFetch` parsed out of a 2xx response; any JSON value, or `undefined`
 *   from a 204.
 * @returns `{ ok: true, session }` with `session` narrowed to `CpCookieSessionResponse` or
 *   `CpTokenPairResponse`, or `{ ok: false, cause }` carrying the `ZodError`.
 * @throws nothing, for any input. `safeParse` is the whole body.
 */
export function parseSessionResponse(body: unknown): SessionParseResult {
  const parsed = cpSessionResponseSchema.safeParse(body);
  return parsed.success ? { ok: true, session: parsed.data } : { ok: false, cause: parsed.error };
}

/**
 * POST a session-minting route under the transport header and parse the answer.
 *
 * ⛔ `apiFetch` DIRECTLY, NOT `api.post`. The verb helpers take no headers, and giving them one
 * would put the transport header one argument away from every call in the package rather than on
 * the calls that mint or end a session. `../http/client.ts` stays unchanged in behaviour.
 *
 * @param path one of the four mint paths that answer a session alone: passkey login, OAuth exchange, password change and the active-role switch. `login` and `secondFactorVerify` parse their own two-arm answers.
 * @param body the route's request body, serialised here.
 * @returns the parsed session.
 * @throws `SessionResponseError` when the 200 body is not a `CpSessionResponse` (R-37); whatever
 *   `apiFetch` throws otherwise, which is `ApiError` for a non-2xx and the raw failure for a
 *   network fault. The sign-in surfaces already render both through `displayMessage`.
 */
async function postMint(path: string, body: unknown): Promise<AuthTokens> {
  const json = await apiFetch<unknown>(path, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: sessionTransportHeaders(),
  });
  const parsed = parseSessionResponse(json);
  if (!parsed.ok) {
    throw new SessionResponseError(SESSION_RESPONSE_ERROR_MESSAGE, { cause: parsed.cause });
  }
  return parsed.session;
}

/** The refresh and logout request body: `{}` on the cookie arm, the legacy token on the body arm. */
function sessionRequestBody(legacyRefreshToken: string | null): string {
  return JSON.stringify(legacyRefreshToken === null ? {} : { refreshToken: legacyRefreshToken });
}

/**
 * POST `/v1/auth/logout` once and answer with the status, so `logout` can read it (R-48).
 *
 * @param body the already-serialised request body, reused byte-for-byte across the repeats.
 * @returns the HTTP status. A 204 resolves through `apiFetch`, so it is reported as 204; every
 *   non-2xx arrives as an `ApiError` and its status is reported.
 * @throws whatever `apiFetch` threw when it was not an `ApiError`, which is a network fault. A
 *   request that produced no status ends the repeat sequence, as a stalled request does in the
 *   DP client.
 */
async function postLogoutOnce(body: string): Promise<number> {
  try {
    await apiFetch<void>('/v1/auth/logout', {
      method: 'POST',
      body,
      headers: sessionTransportHeaders(),
    });
    return 204;
  } catch (cause) {
    if (cause instanceof ApiError) return cause.status;
    throw cause;
  }
}

export function getCapabilities(): Promise<AuthCapabilities> {
  return api.get<AuthCapabilities>('/v1/auth/capabilities');
}

/**
 * What a password login answers (kernel apps, WS-9b, locked decision 5).
 *
 * - `session`: the first factor was enough, and the body is a session, as before;
 * - `second-factor`: the server waits for a passkey. `pending` is the `SecondFactorWait` of
 *   `@zion/sdk-contracts` (R-095): a `mode` (`assert` or `enrol` on the CP), the purpose-bound
 *   token and its lifetime in seconds. The caller keeps the token in memory only.
 */
export type LoginResponse =
  | { readonly kind: 'session'; readonly session: AuthTokens }
  | { readonly kind: 'second-factor'; readonly pending: SecondFactorWait };

/** What `parseLoginResponse` answers: the narrowed login answer, or the `ZodError` that refused it. */
export type LoginParseResult =
  | { readonly ok: true; readonly value: LoginResponse }
  | { readonly ok: false; readonly cause: unknown };

/**
 * Parse the 200 body of `POST /v1/auth/login`.
 *
 * It tries `secondFactorWaitSchema` first, then `cpSessionResponseSchema`. Both are `.strict()`, so
 * a body that carries the keys of both arms fails both. It never throws.
 *
 * @returns `{ ok: true, value }`, or `{ ok: false, cause }` with the session parse's `ZodError`.
 */
export function parseLoginResponse(body: unknown): LoginParseResult {
  const wait = secondFactorWaitSchema.safeParse(body);
  if (wait.success) return { ok: true, value: { kind: 'second-factor', pending: wait.data } };
  const session = parseSessionResponse(body);
  return session.ok ? { ok: true, value: { kind: 'session', session: session.session } } : session;
}

/**
 * Exchange an email and password for a session, or for a second-factor wait.
 *
 * @param email what the operator typed, sent verbatim; cp-auth-api owns the normalisation.
 * @param password the plaintext password, over TLS, as every password route works.
 * @returns `{ kind: 'session' }` with the parsed session (under the header cp-auth-api answers a
 *   `CpCookieSessionResponse`; an N-1 pod answers a pair and `useAuth` writes it back to the legacy
 *   key), or `{ kind: 'second-factor' }` with the wait. A wait sets no cookie.
 * @throws `SessionResponseError` on a 200 that neither arm accepts; `ApiError` on a 401 for bad
 *   credentials and on every other non-2xx.
 */
export async function login(email: string, password: string): Promise<LoginResponse> {
  const json = await apiFetch<unknown>('/v1/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
    headers: sessionTransportHeaders(),
  });
  const parsed = parseLoginResponse(json);
  if (!parsed.ok) throw new SessionResponseError(SESSION_RESPONSE_ERROR_MESSAGE, { cause: parsed.cause });
  return parsed.value;
}

/**
 * What a second-factor verify answers.
 *
 * - `session`: the console adopts the session, as after a login;
 * - `redirect`: the wait began in an OAuth callback (`#sf=` link). The browser goes to the console
 *   callback with a one-time code, or to the CLI listener on `http://localhost`.
 *
 * Either arm carries `recoveryCodes` when the verify registered the operator's first passkey and
 * cp-auth-api issued ten recovery codes (kernel apps stage 5, WS-31a1, R-152 Q-5). The server shows
 * them once and never again, so the caller keeps them in memory only and shows them to the
 * operator. The key is absent when the server issued no code. `session` never holds the codes: it
 * is the session body alone, as `setTokens` takes it.
 */
export type SecondFactorVerifyAnswer =
  | { readonly kind: 'session'; readonly session: AuthTokens; readonly recoveryCodes?: readonly string[] }
  | { readonly kind: 'redirect'; readonly redirectUrl: string; readonly recoveryCodes?: readonly string[] };

/**
 * Ask for the WebAuthn options of a second-factor wait: `POST /v1/auth/second-factor/options`.
 *
 * The route is public; the token is the credential. It sends no transport header, because it
 * answers no session.
 *
 * @returns the parsed answer: the ceremony beside its options. The parse refuses options whose
 *   user verification is not `required`.
 * @throws `SessionResponseError` on a 200 that `secondFactorOptionsResponseSchema` refuses;
 *   `ApiError` on a non-2xx (400 `second_factor_token_invalid` for a spent, unknown or expired
 *   token); the raw failure on a network fault.
 */
export async function secondFactorOptions(request: SecondFactorOptionsRequest): Promise<SecondFactorOptionsResponse> {
  const json = await apiFetch<unknown>('/v1/auth/second-factor/options', {
    method: 'POST',
    body: JSON.stringify(request),
  });
  const parsed = secondFactorOptionsResponseSchema.safeParse(json);
  if (!parsed.success) throw new SessionResponseError(SESSION_RESPONSE_ERROR_MESSAGE, { cause: parsed.error });
  return parsed.data;
}

/**
 * Send the WebAuthn result of a second-factor wait: `POST /v1/auth/second-factor/verify`, under the
 * transport header, because a `session` answer mints a session (R-50).
 *
 * It parses the body with `cpSecondFactorVerifyAnswerSchema` of `@zion/sdk-contracts`: three strict
 * arms, the cookie session, the token pair and `{ redirectUrl }` (1 to 4,096 characters), each with
 * an optional list of exactly ten recovery codes (WS-31a1-R3). A body with the keys of two arms or
 * with an unknown key fails every arm.
 *
 * @returns `{ kind: 'session' }` with the session body for a session arm, else `{ kind: 'redirect' }`;
 *   each with `recoveryCodes` when the body carried them.
 * @throws `SessionResponseError` on a 200 that no arm accepts; `ApiError` on a non-2xx (400
 *   with one of the refusals of `secondFactorRefusalOf`); the raw failure on a network fault.
 */
export async function secondFactorVerify(request: SecondFactorVerifyRequest): Promise<SecondFactorVerifyAnswer> {
  const json = await apiFetch<unknown>('/v1/auth/second-factor/verify', {
    method: 'POST',
    body: JSON.stringify(request),
    headers: sessionTransportHeaders(),
  });
  const parsed = cpSecondFactorVerifyAnswerSchema.safeParse(json);
  if (!parsed.success) throw new SessionResponseError(SESSION_RESPONSE_ERROR_MESSAGE, { cause: parsed.error });
  const { recoveryCodes, ...rest } = parsed.data;
  const codes = recoveryCodes === undefined ? {} : { recoveryCodes };
  if ('redirectUrl' in rest) return { kind: 'redirect', redirectUrl: rest.redirectUrl, ...codes };
  return { kind: 'session', session: rest, ...codes };
}

/**
 * The six 400 refusals of the second-factor routes: four of WS-6 and two of the recovery proofs
 * (kernel apps stage 5, WS-31a1, `RECOVERY_REFUSALS` of `@zion/sdk-contracts`):
 *
 * - `token_invalid`: the token is spent, unknown or expired; the wait is over;
 * - `no_challenge`: a verify came before the options; the token stays;
 * - `verification_failed`: the assertion or the registration did not verify; the token stays;
 * - `operator_inactive`: the operator cannot sign in; the wait is over;
 * - `recovery_invalid`: a recovery proof did not match; the token stays until the fifth miss, and
 *   then the server deletes the wait, so the next call answers `token_invalid`;
 * - `recovery_unavailable`: the wait takes no recovery proof of this kind now; the token stays.
 */
export type SecondFactorRefusal =
  | 'token_invalid'
  | 'no_challenge'
  | 'verification_failed'
  | 'operator_inactive'
  | 'recovery_invalid'
  | 'recovery_unavailable';

/** The server `message` of each refusal, in the order `secondFactorRefusalOf` tests them. */
const SECOND_FACTOR_REFUSAL_MESSAGES: ReadonlyArray<readonly [string, SecondFactorRefusal]> = [
  ['second_factor_token_invalid', 'token_invalid'],
  ['second_factor_no_challenge', 'no_challenge'],
  ['Authentication verification failed', 'verification_failed'],
  ['second_factor_operator_inactive', 'operator_inactive'],
  [RECOVERY_REFUSALS.invalid, 'recovery_invalid'],
  [RECOVERY_REFUSALS.unavailable, 'recovery_unavailable'],
];

/**
 * The refusal of a failed second-factor call, or `null`.
 *
 * It reads an `ApiError` with status 400 whose body `message` is a string equal to one of the six
 * server messages. Every other value answers `null`: another status, another message, a `message`
 * array of the strict pipe, and a value that is not an `ApiError`. It never throws. Pure.
 */
export function secondFactorRefusalOf(error: unknown): SecondFactorRefusal | null {
  if (!(error instanceof ApiError) || error.status !== 400) return null;
  const body = error.body;
  if (body === null || typeof body !== 'object' || !('message' in body)) return null;
  const message = (body as { readonly message: unknown }).message;
  const row = SECOND_FACTOR_REFUSAL_MESSAGES.find(([text]) => text === message);
  return row === undefined ? null : row[1];
}

/**
 * Parse the 200 body of a recovery proof: a `SecondFactorWait` whose `mode` is `recovery_enrol`
 * (WS-31a1, R-152). Every passed proof answers that wait with a new token, and the old token is
 * spent. The parse refuses every other mode, because no proof route answers a session or an
 * `assert` wait.
 *
 * @throws `SessionResponseError` with the `ZodError` (or a plain `Error` for a wrong mode) as its
 *   `cause` when the body is not a `recovery_enrol` wait.
 */
function parseRecoveryWait(body: unknown): SecondFactorWait {
  const parsed = secondFactorWaitSchema.safeParse(body);
  if (!parsed.success) throw new SessionResponseError(SESSION_RESPONSE_ERROR_MESSAGE, { cause: parsed.error });
  if (parsed.data.mode !== 'recovery_enrol') {
    throw new SessionResponseError(SESSION_RESPONSE_ERROR_MESSAGE, {
      cause: new Error(`a recovery proof answered the mode ${parsed.data.mode}`),
    });
  }
  return parsed.data;
}

/** POST one recovery proof and parse its `recovery_enrol` wait. No transport header: no session. */
async function postRecoveryProof(path: string, body: unknown): Promise<SecondFactorWait> {
  return parseRecoveryWait(await apiFetch<unknown>(path, { method: 'POST', body: JSON.stringify(body) }));
}

/**
 * Prove an `assert` wait with a recovery code: `POST /v1/auth/second-factor/recovery/code`
 * (WS-31a1, R-150). The route is public; the token in the body is the credential.
 *
 * @param request the wait token and the code as `recoveryCodeKey` normalised it.
 * @returns the `recovery_enrol` wait with its new token. The caller then runs the `register`
 *   ceremony through the options and verify calls.
 * @throws `SessionResponseError` on a 200 that is not a `recovery_enrol` wait; `ApiError` on a
 *   non-2xx: 400 `recovery_invalid`, `recovery_unavailable` or `second_factor_token_invalid`, 400
 *   with a `message` array for a body the strict pipe refuses, 429 from the throttle (5 per 60 s);
 *   the raw failure on a network fault.
 */
export function proveRecoveryCode(request: RecoveryCodeProofRequest): Promise<SecondFactorWait> {
  return postRecoveryProof(SECOND_FACTOR_RECOVERY_ROUTES.code, request);
}

/**
 * Ask the CP to mail a one-time code to the operator's own address:
 * `POST /v1/auth/second-factor/recovery/email/send` (WS-31a3). The body holds the token alone; the
 * CP reads the address from the operator row, never from the request.
 *
 * @returns nothing; the server answers 204.
 * @throws `ApiError` on a non-2xx: 400 `recovery_unavailable` while the mail port refuses to
 *   deliver (until #361), 400 `second_factor_token_invalid`, 429; the raw failure on a network fault.
 */
export async function sendRecoveryEmail(request: RecoveryEmailSendRequest): Promise<void> {
  await apiFetch<void>(SECOND_FACTOR_RECOVERY_ROUTES.emailSend, { method: 'POST', body: JSON.stringify(request) });
}

/**
 * Prove an `assert` wait with the six digits of the recovery mail:
 * `POST /v1/auth/second-factor/recovery/email/verify` (WS-31a3).
 *
 * @returns the `recovery_enrol` wait, as `proveRecoveryCode`.
 * @throws as `proveRecoveryCode`.
 */
export function proveRecoveryEmail(request: RecoveryEmailVerifyRequest): Promise<SecondFactorWait> {
  return postRecoveryProof(SECOND_FACTOR_RECOVERY_ROUTES.emailVerify, request);
}

/**
 * Prove an `assert` wait with a break-glass token that `bin/cp-break-glass` printed once:
 * `POST /v1/auth/second-factor/recovery/break-glass` (WS-31a2, R-152 Q-3).
 *
 * @returns the `recovery_enrol` wait, as `proveRecoveryCode`.
 * @throws as `proveRecoveryCode`.
 */
export function proveBreakGlass(request: RecoveryBreakGlassRequest): Promise<SecondFactorWait> {
  return postRecoveryProof(SECOND_FACTOR_RECOVERY_ROUTES.breakGlass, request);
}

/**
 * True when the console may open `url` after a second-factor verify. The two forms that
 * cp-auth-api's `OAuthService` builds (F-12):
 *
 * 1. the console callback: the origin equals `consoleOrigin` and the path is `/auth/oauth/callback`;
 * 2. the CLI listener: protocol `http:` and host name `localhost`, any port and path.
 *
 * A URL with a user name or a password, a URL that does not parse, and every other URL answer
 * `false`. Pure.
 */
export function isAllowedSecondFactorRedirect(url: string, consoleOrigin: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.username !== '' || parsed.password !== '') return false;
  if (parsed.origin === consoleOrigin && parsed.pathname === '/auth/oauth/callback') return true;
  return parsed.protocol === 'http:' && parsed.hostname === 'localhost';
}

/**
 * Complete a passkey assertion.
 *
 * @param response the credential `startAuthentication()` produced, passed through untouched:
 *   cp-auth-api owns its shape and the console must not re-encode it.
 * @returns the parsed session, as `login`.
 * @throws as `login`.
 */
export function passkeyLogin(response: unknown): Promise<AuthTokens> {
  return postMint('/v1/auth/passkey/login', response);
}

/**
 * Exchange an OAuth authorization code for a session.
 *
 * @param code the `code` query parameter the provider redirect carried.
 * @returns the parsed session, as `login`.
 * @throws as `login`.
 */
export function oauthExchange(code: string): Promise<AuthTokens> {
  return postMint('/v1/auth/oauth/exchange', { code });
}

export function passkeyLoginOptions(email: string): Promise<PasskeyLoginOptions> {
  return api.post<PasskeyLoginOptions>('/v1/auth/passkey/login/options', { email });
}

/**
 * Rotate the operator's password.
 *
 * ⚠️ A MINT ROUTE (R-50). cp-auth-api revokes every session and mints a fresh pair, so this call
 * carries the transport header and converts the tab exactly as a login does. The caller must adopt
 * the answer: the old access token still carries `mustChangePassword`.
 *
 * @param currentPassword the password being replaced.
 * @param newPassword the replacement; cp-auth-api owns the policy.
 * @returns the parsed session for the new pair.
 * @throws as `login`; a wrong `currentPassword` is an `ApiError` 401.
 */
export function changePassword(currentPassword: string, newPassword: string): Promise<AuthTokens> {
  return postMint('/v1/auth/change-password', { currentPassword, newPassword });
}

/**
 * Set the active site role of this session: `POST ACTIVE_ROLE_SWITCH_PATH` of
 * `@zion/sdk-contracts` (plan 2026-10-22-cp-active-role, the switch route). The console calls it
 * from the account menu and the elevation notice, through `useAuth().switchActiveRole`.
 *
 * It is a mint route, so it carries the cookie transport header, and the body names no refresh
 * token: the browser sends the `cp_refresh_token` cookie (R-025 keeps the body token for the CLI).
 *
 * @param role a site role slug that the operator holds now.
 * @param stepUpRequestId the approved `role-elevation` step-up request id, or `null` on the first
 *   send. `null` sends no `stepUpRequestId` key, so `activeRoleSwitchRequestSchema` sees `{ role }`.
 * @returns the parsed session with the new claims.
 * @throws as `login`: `ApiError` 403 with the `step_up_required` body for an elevation that names no
 *   request; `ApiError` 403 `role_not_held` or `step_up_invalid` (`ActiveRoleSwitchRefusalBody`);
 *   `ApiError` 400 for a body the server refuses; `SessionResponseError` for a 200 body that is not
 *   a session; the raw failure of a network fault.
 */
export function switchActiveRole(role: string, stepUpRequestId: string | null): Promise<AuthTokens> {
  const body: ActiveRoleSwitchRequest = stepUpRequestId === null ? { role } : { role, stepUpRequestId };
  return postMint(ACTIVE_ROLE_SWITCH_PATH, body);
}

/**
 * Exchange the refresh credential for a new access token.
 *
 * The body is `{}` on the cookie arm and `{ refreshToken }` while this tab still holds a migrated
 * token, which is `refreshRequestSchema` either way. The cookie, when one exists, is what the
 * browser attaches; script never sees it.
 *
 * @param legacyRefreshToken `getLegacyRefreshToken()`, read inside the refresh lock by the one
 *   caller so a coalesced second caller cannot drop it (R-10).
 * @returns the parsed session. A `CpCookieSessionResponse` is how the console learns cp-auth-api
 *   stripped the field; a `CpTokenPairResponse` is how it learns an N-1 pod answered.
 * @throws `TerminalAuthError(401 | 403)` when the server refused the credential, which is the one
 *   verdict that ends the session; `TransientRefreshError(status)` for every other non-2xx and for
 *   a 200 whose body neither member accepts (R-5); `TransientRefreshError('network')` when the
 *   request produced no status at all (R-42). It never throws `ApiError`: a caller that has to
 *   decide between keeping and ending a session cannot do it from a status code alone.
 */
export async function refreshTokens(legacyRefreshToken: string | null): Promise<AuthTokens> {
  let json: unknown;
  try {
    json = await apiFetch<unknown>('/v1/auth/refresh', {
      method: 'POST',
      body: sessionRequestBody(legacyRefreshToken),
      headers: sessionTransportHeaders(),
    });
  } catch (cause) {
    // ⛔ 401 AND 403 ARE THE ONLY TERMINAL VERDICTS. `apiFetch` also raises `ApiError` with the
    // response's own status for a 200 whose body is not JSON, which is a proxy or a rolling pod
    // and is transient (R-42). Anything that is not an `ApiError` never reached a status at all.
    if (cause instanceof ApiError) {
      if (cause.status === 401 || cause.status === 403) throw new TerminalAuthError(cause.status);
      throw new TransientRefreshError(cause.status);
    }
    throw new TransientRefreshError('network');
  }
  const parsed = parseSessionResponse(json);
  // A 200 says the server did not refute the session, so a body neither member accepts refuses
  // the body and keeps the session (R-5).
  if (!parsed.ok) throw new TransientRefreshError(200);
  return parsed.session;
}

/**
 * Revoke the session server-side, and clear both cookies.
 *
 * The header and the same body as `refreshTokens`. The bearer is whatever `apiFetch` attaches
 * from module memory; `useAuth` mints one first when the held token is absent or expired (D25),
 * because cp-auth-api's `logout` is behind `CpJwtAuthGuard` and a revoke that never runs leaves a
 * 30-day family alive behind a signed-out browser.
 *
 * ⛔ A 400 UNDER THE COOKIE ARM IS AN N-1 POD, AND THE REQUEST REPEATS (R-48). A current pod takes
 * the credential from the cookie and answers 204 or 401. A pod that predates this release requires
 * `refreshToken` in the body and rejects `{}` with a 400, so a 400 with no legacy token identifies
 * it. That one case waits `LOGOUT_RETRY_DELAY_MS` and posts again, `LOGOUT_ATTEMPTS` requests in
 * all. Revoking a family the server already revoked is a no-op, so a repeat costs nothing when the
 * first request did land. Every other status, and the whole body arm, return after one request.
 *
 * @param legacyRefreshToken `getLegacyRefreshToken()`; `null` on the cookie arm.
 * @returns nothing. The status is read for the one case above; the body is never read, and 204
 *   carries none (R-64).
 * @throws whatever `apiFetch` throws on a network fault. The caller swallows it: a failed revoke
 *   must not trap the operator in a shell they asked to leave.
 */
export async function logout(legacyRefreshToken: string | null): Promise<void> {
  const body = sessionRequestBody(legacyRefreshToken);
  for (let attempt = 1; ; attempt += 1) {
    const status = await postLogoutOnce(body);
    if (status !== 400 || legacyRefreshToken !== null || attempt === LOGOUT_ATTEMPTS) return;
    await new Promise((resolve) => setTimeout(resolve, LOGOUT_RETRY_DELAY_MS));
  }
}

/**
 * The caller's own resource grants.
 *
 * ⚠️ Returns an ENVELOPE, not a bare array. The expansion is capped and `truncated` is how a
 * caller tells "complete" from "the first N of many" — a consumer that silently applied a
 * truncated list would UNDER-report the permissions its user holds, which is worse than an
 * error because a missing triple is indistinguishable from a denial.
 */
export function getMyGrants(): Promise<ResourceGrantSnapshotResponse> {
  return api.get<ResourceGrantSnapshotResponse>('/v1/auth/me/grants');
}

export type { ResourceGrantSnapshotResponse };
